import { Injectable, inject, signal, computed } from "@angular/core";
import { injectMutation, injectQueryClient } from "@ngneat/query";
import { ServiceCancelDataService } from "../services/service-cancel-data.service";
import { ServiceCancellationRequest } from "../models/service-cancel.model";

@Injectable({ providedIn: "root" })
export class ServiceCancelState {
  private readonly serviceCancelDataService = inject(ServiceCancelDataService);
  private readonly queryClient = injectQueryClient();
  private readonly mutation = injectMutation();

  readonly serviceId = signal<string | null>(null);
  readonly childId = signal<string | null>(null);

  readonly mutationError = signal<any>(null);
  readonly cancelHttpStatus$ = signal<number | null>(null);

  readonly isBusinessRuleError = computed(() => {
    const error = this.mutationError();
    return error?.status === 409 || error?.code === 2;
  });

  readonly isGeneralCancelError = computed(() => {
    const error = this.mutationError();
    return !!error && error?.status !== 409 && error?.code !== 2;
  });

  readonly cancelServiceMutation = this.mutation({
    mutationFn: async (payload: {
      tenantId: string;
      request: ServiceCancellationRequest;
    }) => {
      this.mutationError.set(null);
      this.cancelHttpStatus$.set(null);
      this.cancelling$.set(true);
      this.cancelSuccess$.set(false);
      return await this.serviceCancelDataService.cancelService(
        payload.tenantId,
        payload.request,
      );
    },
    onSuccess: async (response) => {
      if (response.success) {
        this.cancelling$.set(false);
        this.cancelSuccess$.set(true);
        await this.queryClient.invalidateQueries({
          queryKey: ["services"],
          exact: false,
        });
        await this.queryClient.invalidateQueries({
          queryKey: ["order-detail"],
          exact: false,
        });
        await this.queryClient.invalidateQueries({
          queryKey: ["orders", "services"],
          exact: false,
        });
      }
    },
    onError: (error: any) => {
      this.cancelling$.set(false);
      this.mutationError.set(error);
      this.cancelHttpStatus$.set(error?.status || null);
    },
  });

  readonly cancelling$ = signal(false);
  readonly cancelSuccess$ = signal(false);

  readonly isCancelling = computed(() => this.cancelling$());

  readonly hasCancelSuccess = computed(() => this.cancelSuccess$());

  readonly hasCancelError = computed(() => !!this.mutationError());

  readonly canCancelService = computed(() => !this.isCancelling());

  resetMutationState() {
    this.mutationError.set(null);
    this.cancelHttpStatus$.set(null);
    this.cancelling$.set(false);
    this.cancelSuccess$.set(false);
    this.cancelServiceMutation.reset();
  }

  retryCancelService(payload: {
    tenantId: string;
    request: ServiceCancellationRequest;
  }) {
    this.cancelServiceMutation.mutate(payload);
  }
}
